import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { forEach, head, keys, toNumber, transform } from 'lodash';
import { AnimationEvent } from '@angular/animations';
import { listItem } from '@lockton-apps/shared/ui/lui';
import { DynamicFormCustomEvent } from '../dynamic-field-type/dynamic-field-type.component';

export interface DynamicFormCalcTableRow {
  item: Record<string, any>;
  __meta: DynamicFormCalcTableRowMeta;
}

export interface DynamicFormCalcTableRowMeta {
  id: number;
  valid: boolean;
}

export interface DynamicFormCalcTableCell {
  mask?: string;
  index?: number;
  label?: string;
  addonLeft?: string;
  decimalFormat?: string;
  summarize?: boolean;
}

export interface DynamicFormCalcTableTotal {
  label: string;
  addonLeft?: string;
  addonRight?: string;
  decimalFormat: string;
}

@Component({
    selector: 'lockton-apps-dynamic-form-calc-table',
    templateUrl: './dynamic-form-calc-table.component.html',
    styleUrls: ['./dynamic-form-calc-table.component.scss'],
    animations: [listItem],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DynamicFormCalcTableComponent implements OnChanges {
  @Input() distinctBy!: string;
  @Input() total!: DynamicFormCalcTableTotal;
  @Input() cells!: Record<string, DynamicFormCalcTableCell>;
  @Input() fields!: any[];
  @Input() rows!: DynamicFormCalcTableRow[];
  @Output() edit: EventEmitter<number> = new EventEmitter();
  @Output() delete: EventEmitter<number> = new EventEmitter();
  @Output() customEvent = new EventEmitter<DynamicFormCustomEvent>();
  @Output() animationDone: EventEmitter<AnimationEvent> = new EventEmitter();

  cellWidth!: number;
  totals!: Record<string, number | undefined>;

  ngOnChanges(): void {
    this.setCellWidth(this.rows);
    this.setTotals(this.rows, this.cells);
  }

  trackByFn(index: number, item: DynamicFormCalcTableRow): number {
    return item.__meta.id;
  }

  private setCellWidth(rows: DynamicFormCalcTableRow[]): void {
    this.cellWidth = 100 / keys(head(rows)?.item).length;
  }

  private setTotals(
    rows: DynamicFormCalcTableRow[],
    cells: Record<string, DynamicFormCalcTableCell>
  ): void {
    this.totals = transform(
      rows,
      (
        totals: Record<string, number | undefined>,
        row: DynamicFormCalcTableRow
      ) => {
        forEach(cells, (cell: DynamicFormCalcTableCell, id: string) => {
          totals[id] = cell.summarize
            ? (totals[id] || 0) + toNumber(row.item[id])
            : undefined;
        });
      },
      {}
    );
  }
}
